import { maybe, Maybe, rd, RemoteData } from '@passionware/monads';
import type { Resource } from '__types__';
import { enumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import { asGlobalResourceQuery } from 'v5/api/_common/query/workspace-scoping';
import {
  type ResourceQuery,
  resourceQueryUtils
} from 'v5/api/resource/resource.api';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import type { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';

/** A name being typed for a new resource, to look for resources it may repeat. */
export interface ResourceDuplicateCandidate {
  name: string;
  /** Absent: matches of every resource type. */
  resourceTypeId: Maybe<number>;
}

/** The candidate for a contact's name fields: the first name, then the last one if any. */
export function resourceDuplicateCandidate(contact: {
  first_name: string;
  last_name?: Maybe<string>;
  resource_type_id?: Maybe<number>;
}): ResourceDuplicateCandidate {
  return {
    name: `${contact.first_name}${contact.last_name ? ` ${contact.last_name}` : ''}`,
    resourceTypeId: contact.resource_type_id
  };
}

/** `/search/contacts` matches the name as a substring, as the legacy check did; 50 is its default page. */
export function resourceDuplicatesQuery(
  candidate: ResourceDuplicateCandidate
): ResourceQuery {
  return asGlobalResourceQuery(
    resourceQueryUtils.setFilter(
      {
        ...resourceQueryUtils.ofEmptySearchQuery(),
        search: candidate.name,
        page: { page: 0, pageSize: 50 }
      },
      'resource_type_id',
      maybe.map(candidate.resourceTypeId, id => enumFilter.ofOneOf([id]))
    )
  );
}

/**
 * Resources of the whole organisation (a duplicate may sit in another workspace;
 * the backend limits the rows to what the user may see) whose name contains the
 * candidate's; idle while there is no candidate.
 */
export function useResourceDuplicates(
  candidate: Maybe<ResourceDuplicateCandidate>,
  services: WithResourceService
): RemoteData<Resource[]> {
  const query = maybe.isPresent(candidate)
    ? resourceDuplicatesQuery(candidate)
    : maybe.ofAbsent();
  return rd.map(services.resourceService.useList(query), response =>
    rowLevelValidation.validDataFromRows(response.items)
  );
}
